import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { formatLocalDateTime } from '../lib/datetime.js'

// Year timeline for the dashboard (#73). Pure presentational: takes an array
// of EventDto (mixed past + future, any status) and lays them out across
// Jan 1 → Dec 31 of the current calendar year.
//
// Color rules:
//   PUBLISHED + past      → bg-slate-400  (already happened)
//   PUBLISHED + upcoming  → bg-brand-500  (still ahead)
//   CANCELLED             → bg-rose-400 opacity-60
//   DRAFT                 → ring-1 ring-amber-400 bg-white  (outline only)
// Single-day events render as 10px dots; multi-day events (>24h delta) as
// rounded bars whose width spans the duration with a 4px floor so they stay
// visible even on a narrow viewport.

const MONTH_LABELS_FULL = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                           'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MONTH_LABELS_TIGHT = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D']

function classifyEvent(e, now) {
  if (e.status === 'CANCELLED') return 'cancelled'
  if (e.status === 'DRAFT') return 'draft'
  return new Date(e.startsAtUtc).getTime() < now ? 'past' : 'upcoming'
}

const COLOR_CLASSES = {
  past:      'bg-slate-400 hover:bg-slate-500',
  upcoming:  'bg-brand-500 hover:bg-brand-600',
  cancelled: 'bg-rose-400 opacity-60 hover:opacity-80',
  draft:     'ring-1 ring-amber-400 bg-white hover:bg-amber-50',
}

export default function YearTimeline({ events }) {
  const year = new Date().getUTCFullYear()
  const yearStart = useMemo(() => Date.UTC(year, 0, 1), [year])
  const yearEnd = useMemo(() => Date.UTC(year + 1, 0, 1), [year])
  const yearMs = yearEnd - yearStart
  const now = Date.now()
  const todayPct = ((now - yearStart) / yearMs) * 100

  const items = useMemo(() => {
    if (!Array.isArray(events)) return []
    return events
      .filter((e) => e?.startsAtUtc)
      .map((e) => ({ e, startMs: new Date(e.startsAtUtc).getTime(),
                        endMs: new Date(e.endsAtUtc || e.startsAtUtc).getTime() }))
      .filter((x) => x.startMs >= yearStart && x.startMs < yearEnd)
      .sort((a, b) => a.startMs - b.startMs)
  }, [events, yearStart, yearEnd])

  const [selectedId, setSelectedId] = useState(null)
  const containerRef = useRef(null)

  useEffect(() => {
    if (selectedId == null) return
    const onClick = (ev) => {
      if (!containerRef.current?.contains(ev.target)) setSelectedId(null)
    }
    const onKey = (ev) => { if (ev.key === 'Escape') setSelectedId(null) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [selectedId])

  if (items.length === 0) {
    return (
      <div className="py-6 text-sm text-slate-500" data-testid="year-timeline-empty">
        Nothing on the schedule for {year} yet.
      </div>
    )
  }

  return (
    <div ref={containerRef} className="select-none" data-testid="year-timeline">
      {/* Track + markers — overflow-hidden clips any sub-pixel marker overhang
          so the panel never pushes past the viewport on narrow phones (#74). */}
      <div className="relative h-12 w-full overflow-hidden">
        {/* Month gridlines */}
        <div className="absolute inset-0 flex">
          {MONTH_LABELS_FULL.map((_, i) => (
            <div key={i}
                 className="h-full flex-1 border-l border-slate-200 first:border-l-0" />
          ))}
        </div>

        {/* Axis baseline */}
        <div className="absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-slate-200" />

        {/* Today marker */}
        {todayPct >= 0 && todayPct <= 100 ? (
          <div className="absolute top-0 bottom-0"
               style={{ left: `${todayPct}%` }}
               aria-hidden="true">
            <div className="h-full border-l-2 border-dashed border-brand-400" />
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 text-[10px] font-medium uppercase tracking-wide text-brand-500">
              Today
            </div>
          </div>
        ) : null}

        {/* Event markers */}
        {items.map(({ e, startMs, endMs }) => {
          const kind = classifyEvent(e, now)
          const rawLeftPct = ((startMs - yearStart) / yearMs) * 100
          const leftPct = Math.min(100, Math.max(0, rawLeftPct))
          const isMultiDay = endMs - startMs > 24 * 60 * 60 * 1000
          // Clamp multi-day width so left + width never exceeds 100% of the
          // track. Without this, an event ending after Dec 31 could draw a
          // bar past the right edge.
          const widthPct = isMultiDay
            ? Math.max(Math.min(((endMs - startMs) / yearMs) * 100, 100 - leftPct), 0.5)
            : null
          const baseClass = COLOR_CLASSES[kind]
          const sharedStyle = { left: `${leftPct}%` }
          const isSelected = selectedId === e.id

          return (
            <button
              key={e.id}
              type="button"
              data-testid={`evt-${e.id}`}
              data-kind={kind}
              className={[
                'absolute top-1/2 -translate-y-1/2 cursor-pointer rounded-full transition',
                isMultiDay ? 'h-2.5 -translate-x-0' : 'h-2.5 w-2.5 -translate-x-1/2',
                baseClass,
                isSelected ? 'ring-2 ring-brand-300 ring-offset-1' : '',
              ].join(' ')}
              style={isMultiDay
                ? { ...sharedStyle, width: `max(${widthPct}%, 4px)` }
                : sharedStyle}
              onClick={() => {
                if (selectedId !== e.id) {
                  setSelectedId(e.id)
                }
              }}
              onMouseEnter={() => setSelectedId(e.id)}
              onMouseLeave={() => {
                // Desktop hover-out clears unless a click cemented it.
                // We can tell click vs hover by simply leaving it: selectedId
                // is also the click-state, so on mobile it persists; on desktop
                // a fresh hover replaces it cleanly.
              }}
              aria-label={e.title}
            >
              <span className="sr-only">{e.title}</span>
            </button>
          )
        })}
      </div>

      {/* Month labels */}
      <div className="mt-2 flex text-[10px] text-slate-500 sm:text-xs">
        {MONTH_LABELS_FULL.map((m, i) => (
          <div key={i} className="flex-1 text-center">
            <span className="hidden sm:inline">{m}</span>
            <span className="sm:hidden">{MONTH_LABELS_TIGHT[i]}</span>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500">
        <Swatch className="bg-slate-400" label="Past" />
        <Swatch className="bg-brand-500" label="Upcoming" />
        <Swatch className="bg-rose-400 opacity-60" label="Cancelled" />
        <Swatch className="ring-1 ring-amber-400 bg-white" label="Draft" />
      </div>

      {/* Tooltip (one shared surface — content driven by selectedId) */}
      {selectedId ? <Tooltip event={items.find((x) => x.e.id === selectedId)?.e}
                             onDismiss={() => setSelectedId(null)} /> : null}
    </div>
  )
}

function Swatch({ className, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block h-2.5 w-2.5 rounded-full ${className}`} />
      <span>{label}</span>
    </span>
  )
}

function Tooltip({ event, onDismiss }) {
  if (!event) return null
  const place = [event.city, event.country].filter(Boolean).join(', ')
  return (
    <div role="dialog" aria-label={event.title}
         className="mt-3 rounded-lg border border-slate-200 bg-white p-3 text-sm shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to={`/admin/events/${event.id}`}
                onClick={onDismiss}
                className="block truncate font-medium text-slate-900 hover:text-brand-700 hover:underline">
            {event.title}
          </Link>
          <div className="mt-0.5 text-xs text-slate-500">
            {formatLocalDateTime(event.startsAtUtc)}
            {place ? ` · ${place}` : ''}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
            <span className="rounded-full bg-slate-100 px-2 py-0.5">{event.status}</span>
            {event.format ? <span className="rounded-full bg-slate-100 px-2 py-0.5">{event.format}</span> : null}
          </div>
        </div>
        <Link to={`/admin/events/${event.id}`}
              onClick={onDismiss}
              className="shrink-0 rounded-full px-3 py-1 text-xs font-medium text-brand-700 ring-1 ring-brand-200 hover:bg-brand-50">
          Open →
        </Link>
      </div>
    </div>
  )
}
